import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Package, Search, Eye, History, MapPin, Calendar, StickyNote, X, Tag, Filter, QrCode, Printer, ArrowDownCircle, ArrowUpCircle, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { inventoryApi, inventoryCategoriesApi } from "@/lib/unifiedApi";
import { db } from "@/lib/firebase";
import { doc, updateDoc, increment, arrayUnion } from "firebase/firestore";
import ExportDialog from "@/components/ExportDialog";
import { useAuth } from "@/context/AuthContext";
import { logActivity } from "@/lib/activityLog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import QRCode from "react-qr-code";

const DEFAULT_CATEGORIES = ["Equipment", "Supplies", "Electronics", "Furniture", "Tools", "Clothing", "Accessories", "Props", "Other"];

function escapeHtml(str) {
  return String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
}

const INVENTORY_EXPORT_COLUMNS = [
  { key: 'name',        label: 'Name' },
  { key: 'category',    label: 'Category' },
  { key: 'quantity',    label: 'Quantity' },
  { key: 'unit_price',  label: 'Unit Price (฿)' },
  { key: 'total_value', label: 'Total Value (฿)', format: (_, row) => String((row.quantity || 0) * (row.unit_price || 0)) },
  { key: 'location',    label: 'Location' },
  { key: 'date',        label: 'Date Added' },
];

const LOW_STOCK_THRESHOLD = 3;

const getItemQrUrl = (item) => {
  return `${window.location.origin}${window.location.pathname}?item=${item.id}`;
};

const InventoryPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [activeFilter, setActiveFilter] = useState("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [viewLogDialogOpen, setViewLogDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItemForLog, setSelectedItemForLog] = useState(null);

  // Stock movement
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockType, setStockType] = useState("receive");
  const [stockUnit, setStockUnit] = useState("base"); // 'base' | 'package'
  const [selectedItemForStock, setSelectedItemForStock] = useState(null);
  const [stockFormData, setStockFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    quantity: 1,
    note: "",
  });

  // Quick receive (top-level shortcut)
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickSearch, setQuickSearch] = useState("");
  const [quickItemId, setQuickItemId] = useState("");
  const [quickUnit, setQuickUnit] = useState("base");
  const [quickQty, setQuickQty] = useState(1);
  const [quickDate, setQuickDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [quickNote, setQuickNote] = useState("");
  const [quickDropOpen, setQuickDropOpen] = useState(false);

  // QR code
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedItemForQr, setSelectedItemForQr] = useState(null);
  const [scanPickerOpen, setScanPickerOpen] = useState(false);
  const [scanPickerItem, setScanPickerItem] = useState(null);
  const [qrLabelSize, setQrLabelSize] = useState('4x6');
  const qrPrintRef = useRef(null);

  const LABEL_SIZES = [
    { id: '4x6',  label: '4 × 6"',  w: '4in',  h: '6in',  qr: 220 },
    { id: '3x2',  label: '3 × 2"',  w: '3in',  h: '2in',  qr: 120 },
    { id: '2x1',  label: '2 × 1"',  w: '2in',  h: '1in',  qr: 60  },
    { id: 'a4',   label: 'A4',       w: 'a4',   h: '',      qr: 200 },
  ];

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const seedingRef = useRef(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState("");

  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    name: "",
    category: "Equipment",
    quantity: 1,
    unit: "ชิ้น",
    package_unit: "",
    units_per_package: 1,
    unit_price: 0,
    location: "",
    tag_color: "#BFFF00",
    logs: [],
  });

  const [exportFilters, setExportFilters] = useState({ category: 'all' });
  const exportData = items
    .filter((item) => exportFilters.category === 'all' || item.category === exportFilters.category)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const TAG_COLORS = [
    { name: "Lime", value: "#BFFF00" },
    { name: "Red", value: "#EF4444" },
    { name: "Orange", value: "#F97316" },
    { name: "Yellow", value: "#EAB308" },
    { name: "Green", value: "#22C55E" },
    { name: "Teal", value: "#14B8A6" },
    { name: "Blue", value: "#3B82F6" },
    { name: "Purple", value: "#A855F7" },
    { name: "Pink", value: "#EC4899" },
    { name: "Gray", value: "#6B7280" },
  ];

  const [logFormData, setLogFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    location: "",
    note: "",
  });

  useEffect(() => {
    localStorage.removeItem('inventory_categories');
    const unsubscribe = inventoryCategoriesApi.subscribe(async (data) => {
      if (data.length === 0) {
        if (seedingRef.current) return;
        seedingRef.current = true;
        try {
          await Promise.all(
            DEFAULT_CATEGORIES.map((name) =>
              inventoryCategoriesApi.create({ name, order: DEFAULT_CATEGORIES.indexOf(name) })
            )
          );
        } catch (err) {
          console.error("Failed to seed categories:", err);
          setCategories(DEFAULT_CATEGORIES.map((name, idx) => ({ id: `mem-${idx}`, name })));
        } finally {
          seedingRef.current = false;
        }
      } else {
        setCategories(data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = inventoryApi.subscribe((data) => {
      setItems(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Handle QR scan URL params — show action picker (เบิก / คืน)
  const qrHandledRef = useRef(false);
  useEffect(() => {
    if (items.length === 0 || qrHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const itemId = params.get('item');
    const action = params.get('action');
    if (itemId) {
      const found = items.find(i => i.id === itemId);
      if (found) {
        qrHandledRef.current = true;
        window.history.replaceState({}, '', window.location.pathname);
        if (action === 'receive') {
          openStockDialog(found, 'receive');
        } else {
          setScanPickerItem(found);
          setScanPickerOpen(true);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ---- Stock movement ----
  const openStockDialog = (item, type) => {
    setSelectedItemForStock(item);
    setStockType(type);
    setStockUnit("base");
    setStockFormData({
      date: format(new Date(), "yyyy-MM-dd"),
      quantity: 1,
      note: "",
    });
    setStockDialogOpen(true);
  };

  const handleStockMovement = async (e) => {
    e.preventDefault();
    if (!selectedItemForStock) return;
    const inputQty = parseInt(stockFormData.quantity) || 0;
    if (inputQty <= 0) { toast.error("จำนวนต้องมากกว่า 0"); return; }

    const perPkg = (selectedItemForStock.units_per_package > 1 && stockUnit === 'package')
      ? (selectedItemForStock.units_per_package || 1)
      : 1;
    const actualQty = inputQty * perPkg; // qty in base units
    const baseUnit = selectedItemForStock.unit || 'ชิ้น';
    const pkgLabel = stockUnit === 'package'
      ? `${inputQty} ${selectedItemForStock.package_unit} (= ${actualQty} ${baseUnit})`
      : `${inputQty} ${baseUnit}`;

    try {
      const isPositive = stockType === 'receive' || stockType === 'return';
      const delta = isPositive ? actualQty : -actualQty;
      const newQty = Math.max(0, (selectedItemForStock.quantity || 0) + delta);
      const defaultNote = stockType === 'receive' ? `รับเข้า ${pkgLabel}`
        : stockType === 'return' ? `คืนของ ${pkgLabel}`
        : `เบิกออก ${pkgLabel}`;
      const newLog = {
        id: `log-${Date.now()}`,
        date: stockFormData.date,
        type: stockType,
        quantity_delta: delta,
        unit_label: pkgLabel,
        location: selectedItemForStock.location ?? '',
        note: stockFormData.note || defaultNote,
        userId: user?.id || '',
        userName: user?.name || '',
      };
      await updateDoc(doc(db, 'bizops_inventory', selectedItemForStock.id), {
        quantity: increment(delta),
        logs: arrayUnion(newLog),
      });
      toast.success(stockType === 'receive'
        ? `รับเข้า ${pkgLabel} สำเร็จ`
        : stockType === 'return'
        ? `คืนของ ${pkgLabel} สำเร็จ`
        : `เบิกออก ${pkgLabel} สำเร็จ`);
      const mvAction = stockType === 'receive' ? 'inventory_receive' : stockType === 'return' ? 'inventory_return' : 'inventory_dispense';
      logActivity(user?.id, user?.name, mvAction, `${selectedItemForStock.name} ${pkgLabel}`);
      setStockDialogOpen(false);
    } catch (error) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  // ---- Quick receive ----
  const quickSelectedItem = items.find(i => i.id === quickItemId) || null;
  const quickFilteredItems = items.filter(i =>
    !quickSearch || i.name?.toLowerCase().includes(quickSearch.toLowerCase())
  ).slice(0, 8);

  const quickPerPkg = (quickSelectedItem?.units_per_package > 1 && quickUnit === 'package')
    ? (quickSelectedItem.units_per_package || 1) : 1;
  const quickActualQty = (parseInt(quickQty) || 0) * quickPerPkg;
  const quickNewQty = quickSelectedItem
    ? Math.max(0, (quickSelectedItem.quantity || 0) + quickActualQty) : 0;

  const selectQuickItem = (item) => {
    setQuickItemId(item.id);
    setQuickSearch(item.name);
    setQuickDropOpen(false);
    setQuickUnit(item.package_unit && item.units_per_package > 1 ? 'package' : 'base');
    setQuickQty(1);
  };

  const resetQuick = () => {
    setQuickOpen(false);
    setQuickSearch("");
    setQuickItemId("");
    setQuickUnit("base");
    setQuickQty(1);
    setQuickDate(format(new Date(), "yyyy-MM-dd"));
    setQuickNote("");
    setQuickDropOpen(false);
  };

  const handleQuickReceive = async (e) => {
    e.preventDefault();
    if (!quickSelectedItem) { toast.error("เลือกสินค้าก่อน"); return; }
    const inputQty = parseInt(quickQty) || 0;
    if (inputQty <= 0) { toast.error("จำนวนต้องมากกว่า 0"); return; }
    const baseUnit = quickSelectedItem.unit || 'ชิ้น';
    const pkgLabel = quickUnit === 'package' && quickSelectedItem.package_unit
      ? `${inputQty} ${quickSelectedItem.package_unit} (= ${quickActualQty} ${baseUnit})`
      : `${inputQty} ${baseUnit}`;
    try {
      const newLog = {
        id: `log-${Date.now()}`,
        date: quickDate,
        type: 'receive',
        quantity_delta: quickActualQty,
        unit_label: pkgLabel,
        location: quickSelectedItem.location ?? '',
        note: quickNote || `รับเข้า ${pkgLabel}`,
        userId: user?.id || '',
        userName: user?.name || '',
      };
      await updateDoc(doc(db, 'bizops_inventory', quickSelectedItem.id), {
        quantity: increment(quickActualQty),
        logs: arrayUnion(newLog),
      });
      toast.success(`รับเข้า ${pkgLabel}`);
      logActivity(user?.id, user?.name, 'inventory_receive', `${quickSelectedItem.name} ${pkgLabel}`);
      resetQuick();
    } catch {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  // ---- QR ----
  const openQrDialog = (item) => {
    setSelectedItemForQr(item);
    setQrDialogOpen(true);
  };

  const handlePrintQr = () => {
    const win = window.open('', '_blank');
    const url = getItemQrUrl(selectedItemForQr);
    const item = selectedItemForQr;
    const sizeConfig = LABEL_SIZES.find(s => s.id === qrLabelSize) || LABEL_SIZES[0];
    const today = new Date();
    const printDate = `${String(today.getDate()).padStart(2,'0')}/${String(today.getMonth()+1).padStart(2,'0')}/${today.getFullYear()}`;
    const pageSize = sizeConfig.id === 'a4' ? 'a4' : `${sizeConfig.w} ${sizeConfig.h}`;
    const qrSize = sizeConfig.qr;
    const safeName = escapeHtml(item?.name);
    const safeCat  = escapeHtml(item?.category);
    const safeUrl  = escapeHtml(url);
    const safePkg  = (item?.package_unit && item?.units_per_package > 1)
      ? `<p class="pkg">1 ${escapeHtml(item.package_unit)} = ${escapeHtml(String(item.units_per_package))} ${escapeHtml(item.unit)}</p>` : '';
    win.document.write(`
      <html><head><title>QR - ${safeName}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        @page { size: ${pageSize}; margin: 0; }
        body { width:${sizeConfig.w}; ${sizeConfig.h ? `height:${sizeConfig.h};` : ''} display:flex; align-items:center; justify-content:center; font-family:Arial,sans-serif; background:#fff; overflow:hidden; }
        .label { width:100%; height:100%; display:flex; flex-direction:column; border:2px solid #1a1a1a; }
        .header { background:#1a1a1a; color:#fff; display:flex; justify-content:space-between; align-items:center; padding:${qrSize < 100 ? '4px 8px' : '8px 12px'}; flex-shrink:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        .header-co { font-size:${qrSize < 100 ? '7px' : '9px'}; font-weight:bold; letter-spacing:0.5px; }
        .header-cat { font-size:${qrSize < 100 ? '7px' : '9px'}; color:#aaa; }
        .body { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:${qrSize < 100 ? '6px' : '14px 12px 10px'}; gap:${qrSize < 100 ? '5px' : '10px'}; }
        #qr { border:2px solid #1a1a1a; padding:${qrSize < 100 ? '3px' : '6px'}; background:#fff; flex-shrink:0; }
        .name { font-size:${qrSize < 100 ? '8px' : qrSize < 150 ? '11px' : '13px'}; font-weight:bold; color:#1a1a1a; text-align:center; line-height:1.3; }
        .pkg { font-size:${qrSize < 100 ? '7px' : '10px'}; color:#555; text-align:center; }
        .footer { display:flex; align-items:center; justify-content:center; gap:4px; font-size:${qrSize < 100 ? '7px' : '9px'}; color:#888; border-top:1px solid #e5e5e5; padding-top:${qrSize < 100 ? '4px' : '8px'}; width:100%; flex-shrink:0; }
        @media print { .no-print { display:none; } }
      </style></head><body>
      <div class="label">
        <div class="header">
          <span class="header-co">FLASHYOURMEME CO., LTD.</span>
          <span class="header-cat">${safeCat}</span>
        </div>
        <div class="body">
          <div id="qr"></div>
          <p class="name">${safeName}</p>
          ${safePkg}
          <div class="footer">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            <span>${printDate}</span>
          </div>
        </div>
      </div>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
      <script>new QRCode(document.getElementById('qr'), { text: "${safeUrl}", width:${qrSize}, height:${qrSize}, correctLevel: QRCode.CorrectLevel.M }); setTimeout(()=>window.print(), 800);</script>
      </body></html>
    `);
    win.document.close();
  };

  // ---- CRUD ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        // Only update editable metadata — never touch quantity or logs (prevents lost-update race)
        const { quantity, logs, ...editableFields } = formData;
        await inventoryApi.update(editingItem.id, editableFields);
        toast.success("อัปเดตแล้ว");
        logActivity(user?.id, user?.name, 'inventory_update', formData.name || editingItem.id);
      } else {
        const initialLog = {
          id: `log-${Date.now()}`,
          date: formData.date,
          location: formData.location,
          note: `เริ่มต้น — ${formData.quantity} ชิ้น ราคา ฿${formData.unit_price} ต่อชิ้น`,
          type: "initial",
          quantity_delta: formData.quantity,
        };
        const itemData = { ...formData, logs: [initialLog], created_at: new Date().toISOString(), created_by: user?.id || '', created_by_name: user?.name || '' };
        await inventoryApi.create(itemData);
        toast.success("เพิ่มสินค้าแล้ว");
        logActivity(user?.id, user?.name, 'inventory_create', formData.name || '');
      }
      resetForm();
    } catch (error) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await inventoryApi.update(item.id, { ...item, active: item.active === false ? true : false });
    } catch { toast.error("เกิดข้อผิดพลาด"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("ลบสินค้านี้?")) return;
    try {
      const target = items.find(i => i.id === id);
      await inventoryApi.delete(id);
      toast.success("ลบแล้ว");
      logActivity(user?.id, user?.name, 'inventory_delete', target?.name || id);
    } catch (error) {
      toast.error("ลบไม่สำเร็จ");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      date: item.date || format(new Date(), "yyyy-MM-dd"),
      name: item.name || "",
      category: item.category || "Equipment",
      quantity: item.quantity || 1,
      unit: item.unit || "ชิ้น",
      package_unit: item.package_unit || "",
      units_per_package: item.units_per_package || 1,
      unit_price: item.unit_price || 0,
      location: item.location || "",
      tag_color: item.tag_color || "#BFFF00",
      logs: item.logs || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      date: format(new Date(), "yyyy-MM-dd"),
      name: "",
      category: "Equipment",
      quantity: 1,
      unit: "ชิ้น",
      package_unit: "",
      units_per_package: 1,
      unit_price: 0,
      location: "",
      tag_color: "#BFFF00",
      logs: [],
    });
    setDialogOpen(false);
  };

  const openAddLog = (item) => {
    setSelectedItemForLog(item);
    setLogFormData({ date: format(new Date(), "yyyy-MM-dd"), location: "", note: "" });
    setLogDialogOpen(true);
  };

  const openViewLogs = (item) => {
    setSelectedItemForLog(item);
    setViewLogDialogOpen(true);
  };

  const handleAddLog = async (e) => {
    e.preventDefault();
    if (!selectedItemForLog) return;
    try {
      const newLog = {
        id: `log-${Date.now()}`,
        date: logFormData.date,
        location: logFormData.location,
        note: logFormData.note,
        type: "movement",
      };
      const updatedLogs = [...(selectedItemForLog.logs || []), newLog];
      await inventoryApi.update(selectedItemForLog.id, { ...selectedItemForLog, logs: updatedLogs, location: logFormData.location });
      toast.success("บันทึกแล้ว");
      setLogDialogOpen(false);
      setSelectedItemForLog(null);
    } catch (error) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  const handleDeleteLog = async (item, logId) => {
    if (!window.confirm("ลบ log นี้?")) return;
    try {
      const updatedLogs = (item.logs || []).filter(log => log.id !== logId);
      await inventoryApi.update(item.id, { ...item, logs: updatedLogs });
      setSelectedItemForLog({ ...item, logs: updatedLogs });
      toast.success("ลบ log แล้ว");
    } catch (error) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  // ---- Categories ----
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    if (categories.some(c => c.name.toLowerCase() === newCategory.trim().toLowerCase())) {
      toast.error("มีหมวดหมู่นี้แล้ว");
      return;
    }
    try {
      await inventoryCategoriesApi.create({ name: newCategory.trim(), order: categories.length });
      setNewCategory("");
      toast.success("เพิ่มหมวดหมู่แล้ว");
    } catch (err) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  const handleUpdateCategory = async (id) => {
    if (!editCategoryValue.trim()) return;
    try {
      await inventoryCategoriesApi.update(id, { name: editCategoryValue.trim() });
      setEditingCategory(null);
      setEditCategoryValue("");
      toast.success("อัปเดตแล้ว");
    } catch (err) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  const handleDeleteCategory = async (id) => {
    const cat = categories.find(c => c.id === id);
    if (!window.confirm(`ลบ "${cat?.name}"?`)) return;
    try {
      await inventoryCategoriesApi.delete(id);
      toast.success("ลบแล้ว");
    } catch (err) {
      toast.error("เกิดข้อผิดพลาด");
    }
  };

  // ---- Filter / sort ----
  const filteredItems = items
    .filter((item) => {
      const matchesSearch =
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
      const isActive = item.active !== false;
      const matchesActive = activeFilter === "all" ? true : activeFilter === "active" ? isActive : !isActive;
      return matchesSearch && matchesCategory && matchesActive;
    })
    .sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      if (sortOrder === "newest") {
        if (dateB - dateA !== 0) return dateB - dateA;
        const timeA = a.created_at ? new Date(a.created_at) : 0;
        const timeB = b.created_at ? new Date(b.created_at) : 0;
        return timeB - timeA;
      } else {
        if (dateA - dateB !== 0) return dateA - dateB;
        const timeA = a.created_at ? new Date(a.created_at) : 0;
        const timeB = b.created_at ? new Date(b.created_at) : 0;
        return timeA - timeB;
      }
    });

  const getTotalValue = () => items.reduce((sum, item) => sum + (item.quantity * item.unit_price || 0), 0);
  const lowStockCount = items.filter(i => (i.quantity || 0) <= LOW_STOCK_THRESHOLD && (i.quantity || 0) > 0).length;

  const stockPerPkg = (selectedItemForStock?.units_per_package > 1 && stockUnit === 'package')
    ? (selectedItemForStock?.units_per_package || 1)
    : 1;
  const stockActualQty = (parseInt(stockFormData.quantity) || 0) * stockPerPkg;
  const newQtyPreview = selectedItemForStock
    ? Math.max(0, (selectedItemForStock.quantity || 0) + ((stockType === 'receive' || stockType === 'return') ? stockActualQty : -stockActualQty))
    : 0;

  const getLogBadge = (log) => {
    if (log.type === 'initial') return { label: 'เริ่มต้น', cls: 'bg-blue-100 text-blue-700' };
    if (log.type === 'receive') return { label: 'รับเข้า', cls: 'bg-emerald-100 text-emerald-700' };
    if (log.type === 'return')  return { label: 'คืนของ',  cls: 'bg-purple-100 text-purple-700' };
    if (log.type === 'issue')   return { label: 'เบิกออก', cls: 'bg-orange-100 text-orange-700' };
    return { label: 'ย้ายที่', cls: 'bg-gray-100 text-gray-600' };
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Inventory</h1>
          <p className="text-gray-500 mt-1">จัดการอุปกรณ์ — รับเข้า เบิกออก ติดตาม stock</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button
            onClick={() => setQuickOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full"
          >
            <TrendingUp size={18} className="mr-2" />
            รับสินค้าเข้า
          </Button>
          <ExportDialog
            title="Export Inventory"
            filename="inventory"
            data={exportData}
            columns={INVENTORY_EXPORT_COLUMNS}
            recordLabel="items"
          >
            <div className="space-y-1.5">
              <Label className="text-gray-700 text-sm">Category</Label>
              <Select value={exportFilters.category} onValueChange={(v) => setExportFilters((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">ทุกหมวดหมู่</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </ExportDialog>

          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-gray-200">
                <Tag size={18} className="mr-2" />
                Categories
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <Tag size={18} className="text-blue-500" />
                  จัดการหมวดหมู่
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="เพิ่มหมวดหมู่ใหม่..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                    className="border-gray-200"
                  />
                  <Button onClick={handleAddCategory} className="bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                    <Plus size={18} />
                  </Button>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      {editingCategory === cat.id ? (
                        <>
                          <Input value={editCategoryValue} onChange={(e) => setEditCategoryValue(e.target.value)} className="flex-1 border-gray-200 h-8" autoFocus />
                          <Button size="sm" onClick={() => handleUpdateCategory(cat.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingCategory(null); setEditCategoryValue(""); }} className="h-8"><X size={14} /></Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-gray-700">{cat.name}</span>
                          <button onClick={() => { setEditingCategory(cat.id); setEditCategoryValue(cat.name); }} className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"><Pencil size={14} /></button>
                          <button onClick={() => handleDeleteCategory(cat.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-inventory-btn" className="bg-[#BFFF00] hover:bg-[#a6e600] text-black font-medium rounded-full" onClick={() => resetForm()}>
                <Plus size={18} className="mr-2" />
                เพิ่มสินค้า
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900">{editingItem ? "แก้ไขสินค้า" : "เพิ่มสินค้าใหม่"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" />วันที่</Label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">ชื่อสินค้า</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="ชื่อสินค้า" required className="border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">หมวดหมู่</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {categories.map((cat) => (<SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">จำนวน (ตั้งต้น)</Label>
                    <Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} required className="border-gray-200" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">หน่วยพื้นฐาน</Label>
                    <Input value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} placeholder="เช่น ชิ้น, ม้วน, อัน" className="border-gray-200" />
                  </div>
                </div>
                {/* Packaging unit section */}
                <div className="p-3 bg-blue-50 rounded-xl space-y-3">
                  <p className="text-xs font-semibold text-blue-700">หน่วยแพ็ก (ไม่จำเป็น)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-gray-600 text-xs">ชื่อแพ็ก</Label>
                      <Input value={formData.package_unit} onChange={(e) => setFormData({ ...formData, package_unit: e.target.value })} placeholder="เช่น กล่อง, แพ็ก, ชุด" className="border-gray-200 bg-white text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-gray-600 text-xs">1 {formData.package_unit || 'แพ็ก'} = กี่ {formData.unit || 'ชิ้น'}</Label>
                      <Input type="number" min="1" value={formData.units_per_package} onChange={(e) => setFormData({ ...formData, units_per_package: parseInt(e.target.value) || 1 })} className="border-gray-200 bg-white text-sm" />
                    </div>
                  </div>
                  {formData.package_unit && formData.units_per_package > 1 && (
                    <p className="text-xs text-blue-600">1 {formData.package_unit} = {formData.units_per_package} {formData.unit || 'ชิ้น'}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">ราคาต่อ{formData.unit || 'ชิ้น'} (฿)</Label>
                    <Input type="number" step="0.01" min="0" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} required className="border-gray-200" />
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">มูลค่ารวม</span>
                    <span className="text-lg font-semibold text-emerald-600">฿{((formData.quantity || 0) * (formData.unit_price || 0)).toLocaleString()}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-1.5"><MapPin size={14} className="text-red-500" />ที่เก็บ</Label>
                  <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="เก็บที่ไหน?" required className="border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-1.5"><Tag size={14} className="text-purple-500" />สีแท็ก</Label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_COLORS.map((color) => (
                      <button key={color.value} type="button" onClick={() => setFormData({ ...formData, tag_color: color.value })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${formData.tag_color === color.value ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'}`}
                        style={{ backgroundColor: color.value }} title={color.name} />
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-200">ยกเลิก</Button>
                  <Button type="submit" className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black">{editingItem ? "อัปเดต" : "เพิ่มสินค้า"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">รายการทั้งหมด</p>
            <p className="text-2xl font-bold text-gray-900">{items.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">จำนวนรวม</p>
            <p className="text-2xl font-bold text-gray-900">{items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</p>
          </CardContent>
        </Card>
        <Card className={`border shadow-sm ${lowStockCount > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-gray-100'}`}>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">stock ใกล้หมด</p>
            <div className="flex items-center gap-2">
              <p className={`text-2xl font-bold ${lowStockCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{lowStockCount}</p>
              {lowStockCount > 0 && <AlertTriangle size={18} className="text-red-500" />}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">มูลค่ารวม</p>
            <p className="text-2xl font-bold text-[#BFFF00]">฿{getTotalValue().toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input placeholder="ค้นหาชื่อ, หมวดหมู่, ที่เก็บ..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white border-gray-200 rounded-xl" />
        </div>
        <div className="flex rounded-xl border border-gray-200 bg-white overflow-hidden text-xs font-medium">
          {[{id:'active',label:'ใช้งาน'},{id:'inactive',label:'ไม่ใช้'},{id:'all',label:'ทั้งหมด'}].map(o => (
            <button key={o.id} onClick={() => setActiveFilter(o.id)}
              className={`px-3 py-2 transition-colors ${activeFilter === o.id ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              {o.label}
            </button>
          ))}
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[160px] border-gray-200 bg-white rounded-xl">
            <Filter size={16} className="mr-2 text-gray-400" />
            <SelectValue placeholder="ทุกหมวดหมู่" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">ทุกหมวดหมู่</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={setSortOrder}>
          <SelectTrigger className="w-[150px] border-gray-200 bg-white">
            <Calendar size={16} className="mr-2 text-gray-400" />
            <SelectValue placeholder="เรียงตาม" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="newest">ใหม่สุดก่อน</SelectItem>
            <SelectItem value="oldest">เก่าสุดก่อน</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-white border-gray-100 shadow-sm rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          {filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">ไม่พบรายการ</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">วันที่</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">ชื่อสินค้า</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">หมวดหมู่</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">คงเหลือ</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">ราคา/ชิ้น</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">มูลค่า</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">ที่เก็บ</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">รับ / เบิก</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const isLow = (item.quantity || 0) <= LOW_STOCK_THRESHOLD && (item.quantity || 0) > 0;
                    const isEmpty = (item.quantity || 0) === 0;
                    return (
                      <tr key={item.id} className={`border-b border-gray-50 hover:bg-gray-50 ${item.active === false ? 'opacity-40' : ''}`}>
                        <td className="p-4 text-sm text-gray-600">{item.date ? format(parseISO(item.date), "d MMM yyyy") : "-"}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.tag_color || '#BFFF00' }} />
                            <span className="font-medium text-gray-900">{item.name}</span>
                          </div>
                        </td>
                        <td className="p-4"><span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{item.category}</span></td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`font-mono font-bold text-base ${isEmpty ? 'text-red-600' : isLow ? 'text-orange-500' : 'text-gray-900'}`}>
                              {item.quantity || 0}
                            </span>
                            <span className="text-xs text-gray-400">{item.unit || 'ชิ้น'}</span>
                            {item.package_unit && item.units_per_package > 1 && !isEmpty && (
                              <span className="text-xs text-gray-400">
                                ({Math.floor((item.quantity || 0) / item.units_per_package)} {item.package_unit})
                              </span>
                            )}
                            {isEmpty && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">หมด</span>}
                            {isLow && !isEmpty && <AlertTriangle size={13} className="text-orange-400" />}
                          </div>
                        </td>
                        <td className="p-4 font-mono text-gray-900 text-sm">฿{item.unit_price?.toLocaleString()}</td>
                        <td className="p-4 font-mono text-emerald-600 font-medium text-sm">฿{((item.quantity || 0) * (item.unit_price || 0)).toLocaleString()}</td>
                        <td className="p-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><MapPin size={12} className="text-gray-400" />{item.location || "-"}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => openStockDialog(item, 'receive')}
                              className="flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                              title="รับเข้า">
                              <TrendingUp size={13} />รับ
                            </button>
                            <button
                              onClick={() => openStockDialog(item, 'issue')}
                              className="flex items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                              title="เบิกออก">
                              <TrendingDown size={13} />เบิก
                            </button>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openQrDialog(item)} className="p-2 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-purple-600" title="QR Code"><QrCode size={15} /></button>
                            <button onClick={() => openViewLogs(item)} className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="ประวัติ">
                              <span className="flex items-center gap-0.5">
                                <Eye size={15} />
                                {(item.logs || []).length > 0 && <span className="text-xs font-medium">{(item.logs || []).length}</span>}
                              </span>
                            </button>
                            <button onClick={() => handleEdit(item)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700" title="แก้ไข"><Pencil size={15} /></button>
                            <button onClick={() => handleToggleActive(item)}
                              className={`p-2 rounded-lg text-xs font-medium transition-colors ${item.active === false ? 'bg-gray-100 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                              title={item.active === false ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}>
                              {item.active === false ? '▶' : '⏸'}
                            </button>
                            <button onClick={() => handleDelete(item.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600" title="ลบ"><Trash2 size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stock Movement Dialog */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${stockType === 'receive' ? 'text-emerald-700' : stockType === 'return' ? 'text-purple-700' : 'text-orange-700'}`}>
              {stockType === 'receive'
                ? <><ArrowDownCircle size={20} className="text-emerald-500" />รับสินค้าเข้า</>
                : stockType === 'return'
                ? <><ArrowDownCircle size={20} className="text-purple-500" />คืนของ</>
                : <><ArrowUpCircle size={20} className="text-orange-500" />เบิกสินค้าออก</>}
            </DialogTitle>
          </DialogHeader>
          {selectedItemForStock && (
            <div className="mb-1 p-3 bg-gray-50 rounded-xl flex items-center gap-3">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: selectedItemForStock.tag_color || '#BFFF00' }} />
              <div>
                <p className="font-semibold text-gray-900 text-sm">{selectedItemForStock.name}</p>
                <p className="text-xs text-gray-500">{selectedItemForStock.category} · {selectedItemForStock.location || '-'}</p>
              </div>
            </div>
          )}
          <form onSubmit={handleStockMovement} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" />วันที่</Label>
              <Input type="date" value={stockFormData.date} onChange={(e) => setStockFormData({ ...stockFormData, date: e.target.value })} required className="border-gray-200" />
            </div>
            {/* Unit selector — show only when item has a package unit */}
            {selectedItemForStock?.package_unit && selectedItemForStock?.units_per_package > 1 && (
              <div className="space-y-2">
                <Label className="text-gray-700">หน่วยที่{stockType === 'issue' ? 'เบิก' : stockType === 'return' ? 'คืน' : 'รับ'}</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStockUnit('base')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${stockUnit === 'base' ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    ทีละ{selectedItemForStock.unit || 'ชิ้น'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStockUnit('package')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${stockUnit === 'package' ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    ทีละ{selectedItemForStock.package_unit}
                  </button>
                </div>
                {stockUnit === 'package' && (
                  <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                    1 {selectedItemForStock.package_unit} = {selectedItemForStock.units_per_package} {selectedItemForStock.unit || 'ชิ้น'}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-gray-700">
                จำนวน{stockType === 'receive' ? 'ที่รับเข้า' : stockType === 'return' ? 'ที่คืน' : 'ที่เบิกออก'}
                {' '}({stockUnit === 'package' && selectedItemForStock?.package_unit
                  ? selectedItemForStock.package_unit
                  : (selectedItemForStock?.unit || 'ชิ้น')})
              </Label>
              <Input
                type="number" min="1"
                value={stockFormData.quantity}
                onChange={(e) => setStockFormData({ ...stockFormData, quantity: parseInt(e.target.value) || 1 })}
                required className="border-gray-200 text-xl font-mono"
                autoFocus
              />
            </div>
            {/* Stock preview */}
            {selectedItemForStock && (
              <div className={`p-3 rounded-xl border-2 ${stockType === 'receive' ? 'border-emerald-200 bg-emerald-50' : stockType === 'return' ? 'border-purple-200 bg-purple-50' : 'border-orange-200 bg-orange-50'}`}>
                <p className="text-xs text-gray-500 mb-1">คงเหลือ ({selectedItemForStock.unit || 'ชิ้น'})</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-mono text-gray-500">{selectedItemForStock.quantity || 0}</span>
                  <span className="text-gray-400">→</span>
                  <span className={`text-2xl font-mono font-bold ${newQtyPreview === 0 ? 'text-red-600' : newQtyPreview <= LOW_STOCK_THRESHOLD ? 'text-orange-600' : (stockType === 'receive' || stockType === 'return') ? 'text-emerald-700' : 'text-orange-700'}`}>
                    {newQtyPreview}
                  </span>
                  <span className={`text-sm font-semibold ${(stockType === 'receive' || stockType === 'return') ? 'text-emerald-600' : 'text-orange-600'}`}>
                    ({(stockType === 'receive' || stockType === 'return') ? '+' : '-'}{stockActualQty})
                  </span>
                </div>
                {stockUnit === 'package' && selectedItemForStock.package_unit && (
                  <p className="text-xs text-gray-500 mt-1">
                    {stockFormData.quantity || 0} {selectedItemForStock.package_unit} × {selectedItemForStock.units_per_package} = {stockActualQty} {selectedItemForStock.unit || 'ชิ้น'}
                  </p>
                )}
                {newQtyPreview === 0 && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} />stock จะหมด!</p>
                )}
                {newQtyPreview > 0 && newQtyPreview <= LOW_STOCK_THRESHOLD && (
                  <p className="text-xs text-orange-500 mt-1.5 flex items-center gap-1"><AlertTriangle size={12} />stock จะเหลือน้อย</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5">
                <StickyNote size={14} className="text-amber-500" />
                หมายเหตุ — ใช้งานอะไร / มาจากไหน
              </Label>
              <Textarea
                value={stockFormData.note}
                onChange={(e) => setStockFormData({ ...stockFormData, note: e.target.value })}
                placeholder={stockType === 'receive' ? 'เช่น ซื้อเพิ่ม / รับจาก supplier งาน BNK Concert' : stockType === 'return' ? 'เช่น คืนหลังงาน / ของเหลือจากงาน BNK Concert' : 'เช่น เบิกใช้ถ่ายงาน / งาน BNK Concert'}
                className="border-gray-200 min-h-[70px]"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setStockDialogOpen(false)} className="flex-1 border-gray-200">ยกเลิก</Button>
              <Button type="submit" className={`flex-1 text-white ${stockType === 'receive' ? 'bg-emerald-500 hover:bg-emerald-600' : stockType === 'return' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-orange-500 hover:bg-orange-600'}`}>
                {stockType === 'receive' ? 'บันทึกรับเข้า' : stockType === 'return' ? 'บันทึกคืนของ' : 'บันทึกเบิกออก'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-xs p-0 overflow-hidden" ref={qrPrintRef}>
          <DialogHeader className="sr-only">
            <DialogTitle>QR Label</DialogTitle>
          </DialogHeader>
          {selectedItemForQr && (
            <div className="flex flex-col">
              {/* Dark header bar */}
              <div className="bg-[#1a1a1a] px-4 py-3 flex items-center justify-between">
                <span className="text-white text-xs font-bold tracking-wide">FLASHYOURMEME CO., LTD.</span>
                <span className="text-gray-400 text-xs">{selectedItemForQr.category}</span>
              </div>
              {/* QR code area */}
              <div className="flex flex-col items-center gap-3 p-5">
                <div className="p-3 bg-white border-2 border-[#1a1a1a] rounded">
                  <QRCode
                    value={getItemQrUrl(selectedItemForQr)}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#1a1a1a"
                  />
                </div>
                <p className="font-bold text-[#1a1a1a] text-base text-center leading-tight">{selectedItemForQr.name}</p>
                {selectedItemForQr.package_unit && selectedItemForQr.units_per_package > 1 && (
                  <p className="text-xs text-gray-500">1 {selectedItemForQr.package_unit} = {selectedItemForQr.units_per_package} {selectedItemForQr.unit}</p>
                )}
                {/* Footer */}
                <div className="flex items-center gap-1.5 text-xs text-gray-400 border-t border-gray-100 pt-2 w-full justify-center">
                  <Printer size={11} />
                  <span>{format(new Date(), "d MMM yyyy")}</span>
                </div>
              </div>
              <div className="px-5 pb-5 space-y-3">
                {/* Paper size picker */}
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">ขนาดกระดาษ</p>
                  <div className="grid grid-cols-4 gap-1">
                    {LABEL_SIZES.map(s => (
                      <button
                        key={s.id}
                        onClick={() => setQrLabelSize(s.id)}
                        className={`py-1.5 rounded text-xs font-medium border transition-colors ${
                          qrLabelSize === s.id
                            ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={handlePrintQr} className="w-full bg-[#1a1a1a] hover:bg-[#333] text-white">
                  <Printer size={16} className="mr-2" />
                  เปิดหน้าพิมพ์ / บันทึก PDF
                </Button>
                <p className="text-xs text-center text-gray-400">เปิดแล้วกด Save as PDF → เปิดใน label app แล้วพิมพ์</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Log Dialog (location movement only) */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <History size={18} className="text-emerald-500" />
              บันทึกการย้ายที่เก็บ — {selectedItemForLog?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLog} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" />วันที่</Label>
              <Input type="date" value={logFormData.date} onChange={(e) => setLogFormData({ ...logFormData, date: e.target.value })} required className="border-gray-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><MapPin size={14} className="text-red-500" />ย้ายไปที่</Label>
              <Input value={logFormData.location} onChange={(e) => setLogFormData({ ...logFormData, location: e.target.value })} placeholder="ที่เก็บใหม่" required className="border-gray-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><StickyNote size={14} className="text-amber-500" />หมายเหตุ</Label>
              <Textarea value={logFormData.note} onChange={(e) => setLogFormData({ ...logFormData, note: e.target.value })} placeholder="รายละเอียดเพิ่มเติม" className="border-gray-200 min-h-[80px]" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setLogDialogOpen(false)} className="flex-1 border-gray-200">ยกเลิก</Button>
              <Button type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white">บันทึก</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Logs Dialog */}
      <Dialog open={viewLogDialogOpen} onOpenChange={setViewLogDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <History size={18} className="text-blue-500" />
              ประวัติ — {selectedItemForLog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="p-4 bg-gray-50 rounded-xl mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">หมวดหมู่:</span><span className="ml-2 text-gray-900">{selectedItemForLog?.category}</span></div>
                <div><span className="text-gray-500">ที่เก็บปัจจุบัน:</span><span className="ml-2 text-gray-900">{selectedItemForLog?.location}</span></div>
                <div><span className="text-gray-500">คงเหลือ:</span><span className="ml-2 font-bold text-gray-900">{selectedItemForLog?.quantity} ชิ้น</span></div>
                <div><span className="text-gray-500">ราคา/ชิ้น:</span><span className="ml-2 text-gray-900">฿{selectedItemForLog?.unit_price?.toLocaleString()}</span></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">{(selectedItemForLog?.logs || []).length} รายการ</p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => { setViewLogDialogOpen(false); openAddLog(selectedItemForLog); }} className="border-gray-200 text-gray-600 text-xs">
                  <MapPin size={13} className="mr-1" />ย้ายที่เก็บ
                </Button>
                <Button size="sm" onClick={() => { setViewLogDialogOpen(false); openStockDialog(selectedItemForLog, 'receive'); }} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                  <TrendingUp size={13} className="mr-1" />รับเข้า
                </Button>
                <Button size="sm" onClick={() => { setViewLogDialogOpen(false); openStockDialog(selectedItemForLog, 'issue'); }} className="bg-orange-500 hover:bg-orange-600 text-white text-xs">
                  <TrendingDown size={13} className="mr-1" />เบิกออก
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {(selectedItemForLog?.logs || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p>ยังไม่มีประวัติ</p>
                </div>
              ) : (
                [...(selectedItemForLog?.logs || [])]
                  .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.id || '').localeCompare(a.id || ''))
                  .map((log) => {
                  const badge = getLogBadge(log);
                  return (
                    <div key={log.id} className={`p-4 rounded-xl border ${
                      log.type === 'initial' ? 'bg-blue-50 border-blue-100' :
                      log.type === 'receive' ? 'bg-emerald-50 border-emerald-100' :
                      log.type === 'issue' ? 'bg-orange-50 border-orange-100' :
                      'bg-white border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                            {log.quantity_delta !== undefined && (
                              <span className={`text-sm font-mono font-bold ${log.quantity_delta > 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                                {log.quantity_delta > 0 ? '+' : ''}{log.unit_label || `${log.quantity_delta} ชิ้น`}
                              </span>
                            )}
                            {log.userName && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                                {log.userName}
                              </span>
                            )}
                            <span className="text-sm text-gray-500">{log.date ? format(parseISO(log.date), "d MMM yyyy") : "-"}</span>
                          </div>
                          {log.location && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-700 mb-1">
                              <MapPin size={13} className="text-gray-400" />
                              <span>{log.location}</span>
                            </div>
                          )}
                          {log.note && <p className="text-sm text-gray-600 mt-1.5 p-2 bg-white/70 rounded-lg">{log.note}</p>}
                        </div>
                        {log.type !== 'initial' && (
                          <button onClick={() => handleDeleteLog(selectedItemForLog, log.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 ml-2 flex-shrink-0">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Receive Dialog */}
      <Dialog open={quickOpen} onOpenChange={(v) => { if (!v) resetQuick(); }}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2">
              <TrendingUp size={20} className="text-emerald-500" />
              รับสินค้าเข้า
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickReceive} className="space-y-4 mt-1">
            {/* Item search / suggestion */}
            <div className="space-y-2">
              <Label className="text-gray-700">เลือกสินค้า</Label>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                  value={quickSearch}
                  onChange={(e) => {
                    setQuickSearch(e.target.value);
                    setQuickItemId("");
                    setQuickDropOpen(true);
                  }}
                  onFocus={() => setQuickDropOpen(true)}
                  placeholder="พิมพ์ชื่อสินค้า..."
                  className="border-gray-200 pl-9"
                  autoFocus
                />
                {quickDropOpen && quickFilteredItems.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    {quickFilteredItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={() => selectQuickItem(item)}
                        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-2.5 border-b border-gray-50 last:border-0"
                      >
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.tag_color || '#BFFF00' }} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                          <p className="text-xs text-gray-400">
                            คงเหลือ {item.quantity || 0} {item.unit || 'ชิ้น'}
                            {item.package_unit && item.units_per_package > 1
                              ? ` · 1${item.package_unit}=${item.units_per_package}${item.unit}`
                              : ''}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Selected item info */}
            {quickSelectedItem && (
              <div className="p-3 bg-emerald-50 rounded-xl flex items-center gap-3">
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: quickSelectedItem.tag_color || '#BFFF00' }} />
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{quickSelectedItem.name}</p>
                  <p className="text-xs text-gray-500">
                    คงเหลือ {quickSelectedItem.quantity || 0} {quickSelectedItem.unit || 'ชิ้น'}
                    {quickSelectedItem.package_unit && quickSelectedItem.units_per_package > 1
                      ? ` · 1 ${quickSelectedItem.package_unit} = ${quickSelectedItem.units_per_package} ${quickSelectedItem.unit}`
                      : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Unit toggle (only when item has package_unit) */}
            {quickSelectedItem?.package_unit && quickSelectedItem?.units_per_package > 1 && (
              <div className="space-y-2">
                <Label className="text-gray-700">รับเข้าเป็น</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setQuickUnit('package')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${quickUnit === 'package' ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {quickSelectedItem.package_unit}
                  </button>
                  <button type="button" onClick={() => setQuickUnit('base')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${quickUnit === 'base' ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                    {quickSelectedItem.unit || 'ชิ้น'}
                  </button>
                </div>
              </div>
            )}

            {/* Qty input */}
            <div className="space-y-2">
              <Label className="text-gray-700">
                จำนวน ({quickUnit === 'package' && quickSelectedItem?.package_unit
                  ? quickSelectedItem.package_unit
                  : (quickSelectedItem?.unit || 'ชิ้น')})
              </Label>
              <Input
                type="number" min="1"
                value={quickQty}
                onChange={(e) => setQuickQty(parseInt(e.target.value) || 1)}
                className="border-gray-200 text-2xl font-mono text-center h-14"
              />
            </div>

            {/* Preview */}
            {quickSelectedItem && (
              <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50">
                <p className="text-xs text-gray-500 mb-1">คงเหลือ ({quickSelectedItem.unit || 'ชิ้น'})</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-mono text-gray-500">{quickSelectedItem.quantity || 0}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-2xl font-mono font-bold text-emerald-700">{quickNewQty}</span>
                  <span className="text-sm font-semibold text-emerald-600">(+{quickActualQty})</span>
                </div>
                {quickUnit === 'package' && quickSelectedItem.package_unit && quickPerPkg > 1 && (
                  <p className="text-xs text-emerald-700 mt-1 font-medium">
                    {quickQty} {quickSelectedItem.package_unit} × {quickSelectedItem.units_per_package} = {quickActualQty} {quickSelectedItem.unit || 'ชิ้น'}
                  </p>
                )}
              </div>
            )}

            {/* Date + Note */}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" />วันที่</Label>
              <Input type="date" value={quickDate} onChange={(e) => setQuickDate(e.target.value)} className="border-gray-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-1.5"><StickyNote size={14} className="text-amber-500" />หมายเหตุ (ไม่จำเป็น)</Label>
              <Input value={quickNote} onChange={(e) => setQuickNote(e.target.value)} placeholder="เช่น ซื้อเพิ่ม / งานไหน" className="border-gray-200" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={resetQuick} className="flex-1 border-gray-200">ยกเลิก</Button>
              <Button type="submit" disabled={!quickSelectedItem} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50">
                บันทึกรับเข้า
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scan Action Picker — ปรากฏหลังแสกน QR */}
      {scanPickerOpen && scanPickerItem && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setScanPickerOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-6 pb-10 sm:pb-6 space-y-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Item info */}
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: scanPickerItem.tag_color || '#BFFF00' }} />
              <div>
                <p className="font-bold text-gray-900 text-base leading-tight">{scanPickerItem.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {scanPickerItem.category} · คงเหลือ {scanPickerItem.quantity || 0} {scanPickerItem.unit || 'ชิ้น'}
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center">เลือกประเภทการทำรายการ</p>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setScanPickerOpen(false);
                  openStockDialog(scanPickerItem, 'issue');
                }}
                className="flex flex-col items-center gap-2 py-5 rounded-2xl border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors active:scale-95"
              >
                <ArrowUpCircle size={32} className="text-orange-500" />
                <span className="font-bold text-orange-700 text-sm">เบิกออก</span>
                <span className="text-xs text-orange-400">นำของออกไปใช้งาน</span>
              </button>
              <button
                onClick={() => {
                  setScanPickerOpen(false);
                  openStockDialog(scanPickerItem, 'return');
                }}
                className="flex flex-col items-center gap-2 py-5 rounded-2xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors active:scale-95"
              >
                <ArrowDownCircle size={32} className="text-purple-500" />
                <span className="font-bold text-purple-700 text-sm">คืนของ</span>
                <span className="text-xs text-purple-400">นำของกลับคืน stock</span>
              </button>
            </div>

            <button
              onClick={() => setScanPickerOpen(false)}
              className="w-full py-2 text-sm text-gray-400 hover:text-gray-600"
            >
              ยกเลิก
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
